import React, { createContext, useContext, useMemo } from 'react';
import { RFQRole } from '../types';

/**
 * Who may act on the RFQ currently on screen.
 *
 * Every member of the org sees the whole wizard; the creator and anyone they
 * shared it with as an EDITOR may change it. The verdict is server-computed
 * (`rfq.access`) and threaded from the wizard page down to the leaves through
 * this context rather than through five step prop interfaces and twenty
 * components below them.
 *
 * `readOnly` and `role` answer different questions and both are needed:
 * `readOnly` gates the editing controls and an EDITOR is NOT read-only, while
 * `role` gates the one thing an editor may not do — sharing.
 *
 * The default is EDITABLE and OWNER. A payload from before the flags existed,
 * or a component rendered outside the wizard, must behave exactly as it did —
 * a missing flag never locks the owner out of his own RFQ.
 */
export interface WizardAccess {
  /** True when the viewer may look but not touch. False for an editor. */
  readOnly: boolean;
  /** This viewer's standing — only an OWNER may change who has access. */
  role: RFQRole;
  /** Whose RFQ it is, when the record carries a creator name. */
  ownerName: string | null;
}

const DEFAULT_ACCESS: WizardAccess = { readOnly: false, role: 'OWNER', ownerName: null };

const WizardAccessContext = createContext<WizardAccess>(DEFAULT_ACCESS);

export interface WizardAccessProviderProps extends Partial<WizardAccess> {
  children: React.ReactNode;
}

export const WizardAccessProvider: React.FC<WizardAccessProviderProps> = ({
  readOnly = false,
  role = 'OWNER',
  ownerName = null,
  children,
}) => {
  const value = useMemo(() => ({ readOnly, role, ownerName }), [readOnly, role, ownerName]);
  return <WizardAccessContext.Provider value={value}>{children}</WizardAccessContext.Provider>;
};

export const useWizardAccess = (): WizardAccess => useContext(WizardAccessContext);

/** Shorthand for the common case — a component that only needs the verdict. */
export const useReadOnly = (): boolean => useContext(WizardAccessContext).readOnly;

export default WizardAccessContext;
